// A translation group has ONE structure and many contents.
//
// A page is one row per language joined to its siblings by a shared
// `translationKey` (see ./fields), and until this file existed those rows were
// unrelated after the moment `translate` copied one onto the other: adding an
// element to the English page never reached the Spanish one, and element order,
// frames, tags and a `code` element's markup were free to drift apart. That is
// the drift a shared layout exists to prevent.
//
// What is here is the projection and the merge, as pure functions over two
// rows. The model applies them (api/code/src/model/page), the editor labels
// them (app/code/src/routes/.../page/edit/elements), and neither owns the rule.
//
// WHY IT IS A MERGE AND NOT A POINTER. Every row stays whole: a page carries
// its own elements and its own config exactly as it did before, so the render
// path still composes a page from one row, `resolve()` is still one partition
// read, hreflang is still free, a route pack is unaffected and there is nothing
// to migrate. The cost is that sameness is repaired on write rather than made
// impossible — which is why `sameStructure` below exists, and why the model
// propagates only when the structure actually moved.
import * as SchemaLib from "@repoflow/schema";
import PageConfigSchema from "@common/schema/page-config";
import {
  hasTranslatable,
  sameTranslatable,
  sharedOf,
  stable,
  translatableOf,
} from "@common/schema/page-element/translatable";

// Of /PageConfig/Override's properties, the ones each language keeps.
//
// The same line ./page-element/translatable draws inside an element, drawn
// again around the document shell: author-typed text is the language's,
// declarations and markup are the group's.
//
// `metadataList` and `attributes` are here because their contents are prose of
// exactly the kind `values` holds — a shared `metadataList` would give the
// Spanish page an English `og:description`, which is worse than a duplicated
// `og:image` URL.
export const CONFIG_TRANSLATABLE = [
  "title",
  "description",
  "values",
  "metadataList",
  "attributes",
];

// And the rest, which every language in the group holds identically.
//
// Enumerated rather than derived as "the others", so that a property added to
// /PageConfig fails `page-translation-sync.test.js` and has to be assigned a
// side. It is the shape MERGE_RULES uses in @common/render/page for the same
// reason: a property that falls through a list here is one whose behaviour
// nobody decided.
export const CONFIG_SHARED = [
  "faviconUrl",
  "bodyClasses",
  "bodyAttributes",
  "bodyTemplate",
  "inlineHead",
  "inlineStyle",
  "inlineScript",
  "includesStyle",
  "includesScript",
  "types",
];

// Every property the override document declares — what the two lists above are
// asserted to cover between them.
export const configProperties = () =>
  Object.keys(
    SchemaLib.resolve(PageConfigSchema, { $ref: "/PageConfig/Override" }).properties
  );

const pick = (source, keys) =>
  keys.reduce((res, key) => {
    if (source && source[key] !== undefined) {
      res[key] = source[key];
    }

    return res;
  }, {});

// What the group shares, as one comparable value.
//
// The model compares this before and after an update to decide whether there is
// anything to propagate: a translator fixing a paragraph changes nothing here,
// and their save stays the single write it costs today.
export const structureOf = (page) => ({
  elements: ((page && page.elements) || []).map(sharedOf),
  config: pick((page && page.config) || {}, CONFIG_SHARED),
});

export const sameStructure = (left, right) =>
  stable(structureOf(left)) === stable(structureOf(right));

// The target's element that corresponds to this one of the source's.
//
// `elementid` is what joins them, and it already lines up across a group:
// `translate` copies the array wholesale, so every group it made shares ids,
// and every element propagated from here carries the source's id.
//
// The positional fallback is for a row old enough to carry elements with no id
// at all — the field is minted by the client, so nothing guarantees one on a
// row written by an older app. It is deliberately narrow: same position AND
// same type AND the target's is unidentified too, so it can only ever match an
// element that has not been given an identity by either side.
const matchOf = (element, index, elements, byId) => {
  if (element && element.elementid) {
    return byId[element.elementid];
  }

  const at = elements[index];

  return at && !at.elementid && at.type === (element && element.type) ? at : undefined;
};

// The source's elements, carrying the target's translations.
//
// The source decides which elements exist, in what order, and everything about
// each of them but the prose inside it. An element the target has and the
// source does not is dropped — that is what "the group has one structure"
// means, and it is the one destructive thing here.
export const syncedElements = (source, target) => {
  const targetElements = (target && target.elements) || [];
  const byId = targetElements.reduce((res, element) => {
    if (element && element.elementid) {
      res[element.elementid] = element;
    }

    return res;
  }, {});

  return ((source && source.elements) || []).map((element, index) => {
    const found = matchOf(element, index, targetElements, byId);
    // A type change makes the target's content the wrong SHAPE for the element
    // it now is — a paragraph's delta is not a code element's values — so it is
    // treated as a new element rather than merged into.
    const existing = found && found.type === element.type ? found : undefined;
    const shared = sharedOf(element);
    const content = translatableOf(existing || element);

    return {
      ...shared,
      config: { ...shared.config, ...content },
      // An element the target already had keeps whatever it said about itself.
      // A new one arrives holding the SOURCE's prose, which is untranslated by
      // definition — unless there is no prose in it at all, which is every
      // `section` element and every `code` element nobody has filled in.
      translationPending: existing
        ? Boolean(existing.translationPending)
        : hasTranslatable(element),
    };
  });
};

// The source's shell, carrying the target's own text.
export const syncedConfig = (source, target) => ({
  ...(target || {}),
  ...pick(source || {}, CONFIG_SHARED),
});

// Every element that has something to translate, marked as needing it.
//
// What `translate` applies to the row it creates: those elements hold the
// source language's prose verbatim, which is the definition of untranslated.
export const pendingElements = (elements) =>
  (elements || []).map((element) => ({
    ...element,
    translationPending: hasTranslatable(element),
  }));

// The incoming elements, with the mark cleared wherever this save translated
// something.
//
// Server-owned, so a client that knows nothing about the field cannot leave a
// page permanently flagged: an element whose prose differs from what the row
// held has just been translated, and one this save introduced was written in
// this language to begin with. An explicit `false` on the way in still clears
// it — that is the "mark as translated" button, for the translation that is
// legitimately the same string as its source.
export const clearedPending = (incoming, stored) => {
  const byId = ((stored && stored.elements) || []).reduce((res, element) => {
    if (element && element.elementid) {
      res[element.elementid] = element;
    }

    return res;
  }, {});

  return (incoming || []).map((element) => {
    const was = element && element.elementid ? byId[element.elementid] : undefined;

    if (!was || was.type !== element.type) {
      return { ...element, translationPending: false };
    }

    // The same comparison the editor makes as the prose is typed
    // (`translatedNow` in the page editor), from the same function, so the
    // badge and the row cannot disagree about what counts as a translation.
    const unchanged = sameTranslatable(was, element);

    return {
      ...element,
      translationPending: unchanged && element.translationPending !== false
        ? Boolean(was.translationPending)
        : false,
    };
  });
};

// How much of this page is still in somebody else's language.
//
// Only elements with something to translate are counted, so a page made
// entirely of `section` elements reads as done rather than as permanently
// behind — the catalog is what translates those, and it is the site's.
export const translationProgress = (page) => {
  const elements = (page && page.elements) || [];
  const total = elements.filter(hasTranslatable).length;
  const pending = elements.filter(
    (element) => hasTranslatable(element) && element.translationPending
  ).length;

  return { total, pending, translated: total - pending };
};
